import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import labelsData from "../data/labels";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import SquareLogo from "../components/SquareLogo";
import ColorThief from "colorthief";

const Label = () => {
  const { label } = useParams();
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const qrContainerRef = useRef(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [eLabel, setELabel] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [showQRGenerator, setShowQRGenerator] = useState(false);
  const [qrColors, setQrColors] = useState({
    edgeColor: "#B8860B", // Dark gold
    centerColor: "#FFD700", // Bright gold
    backgroundColor: "#FFFFFF", // White
  });
  const [imageRef, setImageRef] = useState(null);

  useEffect(() => {
    if (labelsData[label]) {
      setELabel({
        ...labelsData[label],
        recycleItems: [
          { material: "Tappo", description: "FOR 51 tappo-sughero" },
          { material: "Capsula", description: "PVC 3 capsula-plastica" },
          { material: "Bottiglia", description: "GL 72 bottiglia-vetro" },
        ],
      });
      setError(null);
    } else {
      setELabel(null);
      setError("Etichetta non trovata.");
    }
  }, [label]);

  useEffect(() => {
    if (imageRef && eLabel) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        extractColorsFromImage(img);
      };
      img.src = eLabel.labelImage;
    }
  }, [imageRef, eLabel]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setELabel({ ...eLabel, [name]: value });
  };

  const handleSave = () => {
    setIsEditMode(false);
    console.log("Dati salvati:", eLabel);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleRecycleChange = (e, index) => {
    const { name, value } = e.target;
    const updatedRecycleItems = [...eLabel.recycleItems];
    const itemKey = name.split("-")[0];
    updatedRecycleItems[index][itemKey] = value;
    setELabel({ ...eLabel, recycleItems: updatedRecycleItems });
  };

  const handleColorChange = (e) => {
    const { name, value } = e.target;
    setQrColors((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const extractColorsFromImage = async (imageElement) => {
    try {
      const colorThief = new ColorThief();
      const palette = colorThief.getPalette(imageElement, 10); // Estrai più colori per avere più opzioni

      if (palette && palette.length > 0) {
        // Converti i colori RGB in hex e filtra bianchi/neri
        const colors = palette
          .map((color) => {
            const [r, g, b] = color;
            return {
              hex: `#${r.toString(16).padStart(2, "0")}${g
                .toString(16)
                .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`,
              rgb: [r, g, b],
            };
          })
          .filter((color) => {
            // Filtra colori troppo chiari (bianchi) o troppo scuri (neri)
            const [r, g, b] = color.rgb;
            const brightness = (r * 299 + g * 587 + b * 114) / 1000; // Formula per la luminosità
            const saturation = Math.max(r, g, b) - Math.min(r, g, b); // Semplice calcolo saturazione

            return brightness > 30 && brightness < 220 && saturation > 20;
          })
          .map((color) => color.hex);

        if (colors.length > 0) {
          setQrColors({
            edgeColor: colors[0],
            centerColor: colors[1] || colors[0],
            backgroundColor: "#FFFFFF",
          });
        }
      }
    } catch (error) {
      console.error("Errore nell'estrazione dei colori:", error);
    }
  };

  const downloadQRCode = async () => {
    if (qrRef.current === null) return;

    try {
      const dataUrl = await toPng(qrRef.current, {
        quality: 1.0,
        backgroundColor: qrColors.backgroundColor,
        pixelRatio: 2,
        width: 500,
        height: 500,
      });
      const link = document.createElement("a");
      link.download = `${eLabel.wineName}-QRCode.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Error downloading QR code:", err);
    }
  };

  const renderQRGenerator = () => {
    if (!showQRGenerator) return null;

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Generatore QR Code</h2>
            <button
              onClick={() => setShowQRGenerator(false)}
              style={styles.closeButton}
            >
              ×
            </button>
          </div>

          <div style={styles.qrCodeControls}>
            <button
              onClick={() => extractColorsFromImage(imageRef)}
              style={styles.extractButton}
            >
              Estrai colori dall'immagine
            </button>
            <div style={styles.colorInput}>
              <label>Colore bordo:</label>
              <input
                type="color"
                name="edgeColor"
                value={qrColors.edgeColor}
                onChange={handleColorChange}
              />
            </div>
            <div style={styles.colorInput}>
              <label>Colore centro:</label>
              <input
                type="color"
                name="centerColor"
                value={qrColors.centerColor}
                onChange={handleColorChange}
              />
            </div>
            <div style={styles.colorInput}>
              <label>Colore sfondo:</label>
              <input
                type="color"
                name="backgroundColor"
                value={qrColors.backgroundColor}
                onChange={handleColorChange}
              />
            </div>
          </div>

          <div ref={qrRef} style={styles.qrCodeWrapper}>
            <div ref={qrContainerRef} style={styles.qrCodeContainer}>
              <svg width="500" height="500" style={styles.qrCode}>
                <defs>
                  <radialGradient id="qrGradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={qrColors.centerColor} />
                    <stop offset="100%" stopColor={qrColors.edgeColor} />
                  </radialGradient>
                </defs>
                <QRCodeSVG
                  value={window.location.href}
                  size={500}
                  level="H"
                  bgColor={qrColors.backgroundColor}
                  fgColor="url(#qrGradient)"
                />
              </svg>
              <div style={styles.logoOverlay}>
                <SquareLogo size={120} />
              </div>
            </div>
          </div>

          <button onClick={downloadQRCode} style={styles.downloadButton}>
            Scarica QR Code
          </button>
        </div>
      </div>
    );
  };

  const getAdjacentLabels = () => {
    const labelIds = Object.keys(labelsData);
    const currentIndex = labelIds.indexOf(label);
    return {
      prev: currentIndex > 0 ? labelIds[currentIndex - 1] : null,
      next:
        currentIndex < labelIds.length - 1 ? labelIds[currentIndex + 1] : null,
    };
  };

  if (error) {
    return (
      <div style={styles.container}>
        <h1>{error}</h1>
      </div>
    );
  }

  if (!eLabel) {
    return (
      <div style={styles.container}>
        <h1>Caricamento...</h1>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Navigation Bar */}
      <div style={styles.navigationBar}>
        <Link to="/" style={styles.navButton}>
          ← Torna alla Home
        </Link>
        <button
          onClick={() => setShowQRGenerator(!showQRGenerator)}
          style={styles.qrCodeToggleButton}
        >
          Genera QR
        </button>
      </div>

      {/* Logo Aziendale */}
      <div style={styles.logoContainer}>
        <img
          src={require("../data/media/logo.png")}
          alt="Logo Aziendale"
          style={styles.logo}
        />
      </div>

      {renderQRGenerator()}

      {/* Immagine Etichetta */}
      <div style={styles.imageContainer}>
        <img
          ref={setImageRef}
          src={eLabel.labelImage}
          alt="Etichetta Vino"
          style={styles.image}
          crossOrigin="anonymous"
        />
      </div>

      {/* Contenuto principale */}
      {isEditMode ? (
        <div>
          {renderAccordionContent("Informazioni sul vino", "wine", true)}
          {renderAccordionContent("Ingredienti e allergeni", "allergens", true)}
          {renderAccordionContent("Valori nutrizionali", "nutrition", true)}
          {renderAccordionContent("Riciclo", "recycle", true)}

          <div style={styles.editButtonContainer}>
            <button onClick={handleSave} style={styles.saveButton}>
              Salva
            </button>
          </div>
        </div>
      ) : (
        <div>
          {renderAccordionContent("Informazioni sul vino", "wine")}
          {renderAccordionContent("Ingredienti e allergeni", "allergens")}
          {renderAccordionContent("Valori nutrizionali", "nutrition")}
          {renderAccordionContent("Riciclo", "recycle")}
        </div>
      )}
    </div>
  );

  function renderAccordionContent(header, section, isEditable = false) {
    const isOpen = expandedSections[section];
    return (
      <div style={styles.accordion}>
        <div
          style={styles.accordionHeader}
          onClick={() => toggleSection(section)}
        >
          <h2 style={styles.accordionTitle}>{header}</h2>
          <span style={styles.chevron}>{isOpen ? "▼" : "►"}</span>
        </div>
        {isOpen && (
          <div style={styles.accordionContent}>
            {isEditable
              ? renderEditableContent(section)
              : renderViewContent(section)}
          </div>
        )}
      </div>
    );
  }

  function renderEditableContent(section) {
    switch (section) {
      case "wine":
        return (
          <>
            <input
              type="text"
              name="wineName"
              value={eLabel.wineName}
              onChange={handleChange}
              style={styles.input}
            />
            <input
              type="text"
              name="vintage"
              value={eLabel.vintage}
              onChange={handleChange}
              style={styles.input}
            />
            <input
              type="text"
              name="grapeVarieties"
              value={eLabel.grapeVarieties}
              onChange={handleChange}
              style={styles.input}
            />
            <input
              type="text"
              name="wineType"
              value={eLabel.wineType}
              onChange={handleChange}
              style={styles.input}
            />
          </>
        );
      case "allergens":
        return (
          <>
            <textarea
              name="ingredients"
              value={eLabel.ingredients}
              onChange={handleChange}
              style={styles.textarea}
              placeholder="Ingredienti"
            />
            <textarea
              name="allergens"
              value={eLabel.allergens}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "allergens",
                    value: e.target.value.split(", "),
                  },
                })
              }
              style={styles.textarea}
              placeholder="Allergeni"
            />
          </>
        );
      case "nutrition":
        return (
          <>
            <textarea
              name="nutritionalInformation"
              value={eLabel.nutritionalInformation}
              onChange={handleChange}
              style={styles.textarea}
              placeholder="Valori nutrizionali"
            />
          </>
        );
      case "recycle":
        return (
          <>
            {eLabel.recycleItems.map((item, index) => (
              <div key={index}>
                <input
                  type="text"
                  name={`recycleMaterial-${index}`}
                  value={item.material}
                  onChange={(e) => handleRecycleChange(e, index)}
                  style={styles.input}
                />
                <input
                  type="text"
                  name={`recycleDescription-${index}`}
                  value={item.description}
                  onChange={(e) => handleRecycleChange(e, index)}
                  style={styles.input}
                />
              </div>
            ))}
          </>
        );
      default:
        return null;
    }
  }

  function renderViewContent(section) {
    switch (section) {
      case "wine":
        return (
          <>
            <p>
              <strong>Nome:</strong> {eLabel.wineName}
            </p>
            <p>
              <strong>Annata:</strong> {eLabel.vintage}
            </p>
            <p>
              <strong>Varietà d'uva:</strong> {eLabel.grapeVarieties}
            </p>
            <p>
              <strong>Tipo:</strong> {eLabel.wineType}
            </p>
            <p>
              <strong>Numero di lotto:</strong> {eLabel.lotNumber}
            </p>
          </>
        );
      case "allergens":
        return (
          <>
            <p>
              <strong>Ingredienti: </strong>
              {eLabel.ingredients.length > 0
                ? eLabel.ingredients.join(", ")
                : "Nessuno dichiarato"}
            </p>
            <p>
              <strong>Contiene piccole quantità di: </strong>{" "}
              {eLabel.allergens.length > 0
                ? eLabel.allergens.join(", ")
                : "Nessuno dichiarato"}
            </p>
          </>
        );
      case "nutrition":
        return (
          <table style={styles.table}>
            <thead>
              <tr>
                <th colSpan="2" style={styles.tableHeader}>
                  VALORI NURTRIZIONALI MEDI RIFERITI A 100ml DI PRODOTTO
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>Energia</td>
                <td style={styles.tableCell}>
                  {eLabel.nutrition.energy_jl} kJ{" / "}
                  {eLabel.nutrition.energy_kcal} kcal
                </td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Grassi</td>
                <td style={styles.tableCell}>{eLabel.nutrition.fat} g</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>di cui grassi saturi</td>
                <td style={styles.tableCell}>
                  {eLabel.nutrition.saturatedFat} g
                </td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Carboidrati</td>
                <td style={styles.tableCell}>
                  {eLabel.nutrition.carbohydrates} g
                </td>
              </tr>
              <tr>
                <td style={styles.tableCell}>di cui zuccheri</td>
                <td style={styles.tableCell}>{eLabel.nutrition.sugars} g</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Proteine</td>
                <td style={styles.tableCell}>{eLabel.nutrition.protein} g</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Sale</td>
                <td style={styles.tableCell}>{eLabel.nutrition.salt} g</td>
              </tr>
            </tbody>
          </table>
        );
      case "recycle":
        return (
          <div style={styles.recycleContainer}>
            {eLabel.recycleItems.map((item, index) => (
              <div key={index} style={styles.recycleItem}>
                <p style={styles.recycleText}>
                  <strong>{item.material}:</strong>
                </p>
                <p style={styles.recycleText}>{item.description}</p>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  }
};

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
    boxSizing: "border-box",
  },
  logoContainer: { textAlign: "center", marginBottom: "20px" },
  logo: { maxWidth: "200px", height: "auto" },
  imageContainer: {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    marginBottom: "20px",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
  },
  image: {
    width: "100%",
    height: "auto",
    maxWidth: "600px",
    maxHeight: "400px",
    objectFit: "contain",
  },
  editButtonContainer: { textAlign: "center", marginBottom: "20px" },
  saveButton: {
    padding: "10px 20px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  accordion: { marginBottom: "20px" },
  accordionHeader: {
    display: "flex",
    justifyContent: "space-between",
    cursor: "pointer",
    backgroundColor: "#333",
    color: "#fff",
    padding: "15px",
    borderRadius: "10px",
  },
  accordionTitle: { margin: 0, fontWeight: "bold" },
  chevron: { fontSize: "18px" },
  accordionContent: {
    padding: "15px 20px",
    backgroundColor: "#f7f7f7",
    borderRadius: "10px",
    marginTop: "5px",
  },
  input: {
    width: "100%",
    padding: "10px",
    margin: "10px 0",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },
  textarea: {
    width: "100%",
    height: "100px",
    padding: "10px",
    margin: "10px 0",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },
  table: { width: "100%", borderCollapse: "collapse", margin: "10px 0" },
  tableHeader: {
    backgroundColor: "#444",
    color: "#fff",
    textAlign: "center",
    padding: "10px",
    fontSize: "16px",
  },
  tableCell: { border: "1px solid #ccc", padding: "10px" },
  recycleContainer: { marginTop: "15px" },
  recycleItem: { marginBottom: "10px" },
  recycleText: { margin: "5px 0" },
  qrCodeToggleButton: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    color: "#000",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    ":hover": {
      color: "#666",
      transform: "translateY(-2px)",
    },
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "10px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    maxWidth: "500px",
    width: "90%",
    position: "relative",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    margin: 0,
    color: "#333",
    fontSize: "1.5em",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: "5px",
    lineHeight: 1,
    ":hover": {
      color: "#333",
    },
  },
  qrCodeControls: {
    display: "flex",
    gap: "20px",
    marginBottom: "20px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  colorInput: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "5px",
  },
  extractButton: {
    padding: "10px 20px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    marginBottom: "10px",
    ":hover": {
      backgroundColor: "#45a049",
    },
  },
  qrCodeWrapper: {
    padding: "0px",
    backgroundColor: "transparent",
    margin: "0px",
    display: "inline-block",
    position: "relative",
    width: "500px",
    height: "500px",
  },
  qrCodeContainer: {
    position: "relative",
    display: "block",
    width: "100%",
    height: "100%",
  },
  qrCode: {
    borderRadius: "8px",
  },
  logoOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10,
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  downloadButton: {
    width: "100%",
    padding: "12px 20px",
    backgroundColor: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    ":hover": {
      backgroundColor: "#1976D2",
    },
  },
  navigationBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    backgroundColor: "#f5f5f5",
    marginBottom: "20px",
    borderRadius: "8px",
  },
  navButton: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    color: "#000",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    textDecoration: "none",
    transition: "all 0.3s ease",
    ":hover": {
      color: "#666",
      transform: "translateY(-2px)",
    },
  },
};

export default Label;
